import promiseDelay from './promiseDelay';

/**
 * Waits for a track to report valid width, height, and frameRate in getSettings(),
 * then applies the given constraints.
 *
 * The function first checks if the track already has valid settings. If not, it listens
 * for the track's "unmute" event (which fires when a track starts producing data) while
 * also polling as a fallback. This handles cases where the media processing pipeline
 * (e.g. background blur) takes time to warm up on slow/throttled machines.
 *
 * @param {MediaStreamTrack} track - The video track to apply constraints to.
 * @param {MediaTrackConstraints} constraints - The constraints to apply.
 * @param {number} timeoutMs - Maximum time to wait for the track to be ready (default: 30s).
 * @returns {Promise} Resolves when constraints are applied, rejects on timeout.
 */
const setVideoConstraintsWithRetries = async (track, constraints, timeoutMs = 30000) => {
  const { width, height, frameRate } = track.getSettings();
  if (width && height && frameRate) {
    return track.applyConstraints(constraints);
  }

  // Wait for the track to become ready by listening for the "unmute" event
  // (fires when a muted track starts producing data) combined with polling
  // as a fallback for tracks that are already unmuted but haven't reported
  // dimensions yet.
  return new Promise((resolve, reject) => {
    let settled = false;
    let pollInterval;
    let timeoutId;
    let unmuteHandler;

    const cleanup = () => {
      settled = true;
      clearInterval(pollInterval);
      clearTimeout(timeoutId);
      if (unmuteHandler) {
        track.removeEventListener('unmute', unmuteHandler);
      }
    };

    const tryApply = () => {
      const settings = track.getSettings();
      if (settings.width && settings.height && settings.frameRate) {
        cleanup();
        resolve(track.applyConstraints(constraints));
        return true;
      }
      return false;
    };

    unmuteHandler = () => {
      if (settled) return;
      // Give the track a moment to populate settings after unmute
      promiseDelay(10).then(() => {
        if (settled) return;
        tryApply();
      });
    };

    track.addEventListener('unmute', unmuteHandler);

    // Poll as fallback — some tracks may already be unmuted but just slow
    // to report dimensions (e.g. MediaStreamTrackGenerator from media processor)
    pollInterval = setInterval(() => {
      if (settled) return;
      tryApply();
    }, 50);

    timeoutId = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error(
        'Failed to apply constraints: track did not report width, height, and frameRate in time'
      ));
    }, timeoutMs);

    // One final immediate check in case the track became ready between
    // the initial check and setting up the listeners
    tryApply();
  });
};

export default setVideoConstraintsWithRetries;
